import { useEffect, useState } from "react";
import {
  WhatsappLogo,
  EnvelopeSimple,
  FloppyDisk,
  Bell,
  ShieldCheck,
  Copy,
  Scales,
  DownloadSimple,
  Trash,
  Warning,
} from "@phosphor-icons/react";
import {
  fetchSettings,
  updateSettings,
  dsgvoExport,
  dsgvoForget,
} from "@/lib/api";
import { toast } from "sonner";

export default function Settings() {
  const [data, setData] = useState(null);
  const [webhookBase, setWebhookBase] = useState("");
  const [dsgvo, setDsgvo] = useState({
    sender_id: "",
    channel: "all",
    busy: false,
    confirmDelete: false,
    lastResult: null,
  });

  useEffect(() => {
    fetchSettings().then((s) => setData(s));
    setWebhookBase(`${process.env.REACT_APP_BACKEND_URL}/api/webhooks`);
  }, []);

  const handleDsgvoExport = async () => {
    if (!dsgvo.sender_id.trim()) {
      toast.error("Bitte sender_id eingeben (Telefonnummer oder Email-Adresse).");
      return;
    }
    setDsgvo((s) => ({ ...s, busy: true, lastResult: null }));
    try {
      const result = await dsgvoExport(dsgvo.sender_id.trim(), dsgvo.channel);
      const blob = new Blob([JSON.stringify(result, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = dsgvo.sender_id.replace(/[^a-zA-Z0-9._+-]/g, "_");
      a.href = url;
      a.download = `dsgvo-export-${safeName}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(
        `Export: ${result.summary.conversations} Konv., ${result.summary.messages} Msgs.`,
      );
      setDsgvo((s) => ({ ...s, busy: false, lastResult: { type: "export", ...result.summary } }));
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Export fehlgeschlagen: ${detail}`);
      setDsgvo((s) => ({ ...s, busy: false }));
    }
  };

  const handleDsgvoForget = async () => {
    setDsgvo((s) => ({ ...s, busy: true, confirmDelete: false, lastResult: null }));
    try {
      const result = await dsgvoForget(dsgvo.sender_id.trim(), dsgvo.channel);
      toast.success(
        `Gelöscht: ${result.conversations_deleted} Konv., ${result.messages_deleted} Msgs.`,
      );
      setDsgvo((s) => ({
        ...s,
        busy: false,
        sender_id: "",
        lastResult: {
          type: "forget",
          conversations: result.conversations_deleted,
          messages: result.messages_deleted,
        },
      }));
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Löschung fehlgeschlagen: ${detail}`);
      setDsgvo((s) => ({ ...s, busy: false }));
    }
  };

  const update = (k, v) => setData({ ...data, [k]: v });

  const save = async () => {
    // Send what we have; empty secret = keep existing (backend preserves it)
    const payload = {
      ...data,
      whatsapp_access_token: data.whatsapp_access_token || "",
      whatsapp_app_secret: data.whatsapp_app_secret || "",
      whatsapp_verify_token: data.whatsapp_verify_token || "",
      gmail_app_password: data.gmail_app_password || "",
    };
    await updateSettings(payload);
    toast.success("Settings saved.");
    fetchSettings().then((s) => setData(s));
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard.");
  };

  if (!data) return <div className="p-8 text-muted-foreground">Lade…</div>;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-5xl mx-auto" data-testid="settings-page">
      <div className="mb-8">
        <div className="overline">Einstellungen</div>
        <h1 className="font-semibold text-3xl sm:text-4xl tracking-tight mt-2">
          Integrationen
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">
          Verbinde WhatsApp und Email. Der Bot liefert nur über Kanäle aus,
          die konfiguriert sind. Alle Secrets liegen server-side.
        </p>
      </div>

      {/* WhatsApp */}
      <section className="card-soft mb-6 overflow-hidden" data-testid="settings-whatsapp">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border-soft bg-secondary/30">
          <WhatsappLogo size={18} weight="bold" />
          <div className="overline">Meta WhatsApp Cloud API</div>
        </div>
        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="overline block mb-2">Phone Number ID</label>
            <input
              data-testid="wa-phone-id"
              value={data.whatsapp_phone_number_id || ""}
              onChange={(e) => update("whatsapp_phone_number_id", e.target.value)}
              placeholder="e.g. 123456789012345"
              className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 font-mono transition-all"
            />
          </div>
          <div>
            <label className="overline block mb-2">
              Access Token {data.whatsapp_access_token_masked && (
                <span className="text-muted-foreground">· saved: {data.whatsapp_access_token_masked}</span>
              )}
            </label>
            <input
              data-testid="wa-access-token"
              type="password"
              value={data.whatsapp_access_token || ""}
              onChange={(e) => update("whatsapp_access_token", e.target.value)}
              placeholder={data.whatsapp_access_token_masked ? "Leave blank to keep" : "EAAG... (permanent token)"}
              className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 font-mono transition-all"
            />
          </div>
          <div>
            <label className="overline block mb-2">
              Webhook Verify Token {data.whatsapp_verify_token_masked && (
                <span className="text-muted-foreground">· saved: {data.whatsapp_verify_token_masked}</span>
              )}
            </label>
            <input
              data-testid="wa-verify-token"
              type="password"
              value={data.whatsapp_verify_token || ""}
              onChange={(e) => update("whatsapp_verify_token", e.target.value)}
              placeholder={data.whatsapp_verify_token_masked ? "Leave blank to keep" : "Custom string you choose"}
              className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 font-mono transition-all"
            />
          </div>
          <div>
            <label className="overline block mb-2">
              App Secret {data.whatsapp_app_secret_masked && (
                <span className="text-muted-foreground">· saved: {data.whatsapp_app_secret_masked}</span>
              )}
            </label>
            <input
              data-testid="wa-app-secret"
              type="password"
              value={data.whatsapp_app_secret || ""}
              onChange={(e) => update("whatsapp_app_secret", e.target.value)}
              placeholder={data.whatsapp_app_secret_masked ? "Leave blank to keep" : "From Meta App → Basic Settings"}
              className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 font-mono transition-all"
            />
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Wird zur Verifizierung der HMAC-Signatur (X-Hub-Signature-256)
              jeder Webhook-Anfrage benutzt. Pflicht in Production.
            </p>
          </div>
          <div className="md:col-span-2 p-4 rounded-xl border border-border-soft bg-secondary/40">
            <div className="overline mb-2">Webhook URL (configure in Meta)</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs font-mono break-all bg-white border border-border-soft rounded-lg px-3 py-2">
                {webhookBase}/whatsapp
              </code>
              <button
                className="btn-ghost"
                onClick={() => copy(`${webhookBase}/whatsapp`)}
                data-testid="copy-wa-webhook"
              >
                <Copy size={14} weight="bold" /> Copy
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              In Meta → WhatsApp → Configuration, paste this URL, use your
              verify token above, and subscribe to the <strong>messages</strong>{" "}
              field.
            </p>
          </div>
        </div>
      </section>

      {/* Email */}
      <section className="card-soft mb-6 overflow-hidden" data-testid="settings-email">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border-soft bg-secondary/30">
          <EnvelopeSimple size={18} weight="bold" />
          <div className="overline">Gmail · Email Sending</div>
        </div>
        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="overline block mb-2">Gmail Address</label>
            <input
              data-testid="gmail-address"
              type="email"
              value={data.gmail_address || ""}
              onChange={(e) => update("gmail_address", e.target.value)}
              placeholder="support@yourbusiness.com"
              className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
            />
          </div>
          <div>
            <label className="overline block mb-2">
              App Password {data.gmail_app_password_masked && (
                <span className="text-muted-foreground">· saved: {data.gmail_app_password_masked}</span>
              )}
            </label>
            <input
              data-testid="gmail-password"
              type="password"
              value={data.gmail_app_password || ""}
              onChange={(e) => update("gmail_app_password", e.target.value)}
              placeholder={data.gmail_app_password_masked ? "Leave blank to keep" : "16-char app password"}
              className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 font-mono transition-all"
            />
          </div>
          <div className="md:col-span-2 flex items-start gap-3 p-4 rounded-xl border border-border-soft">
            <input
              data-testid="imap-polling-enabled"
              type="checkbox"
              checked={data.imap_polling_enabled ?? false}
              onChange={(e) => update("imap_polling_enabled", e.target.checked)}
              className="w-4 h-4 mt-0.5"
            />
            <div>
              <div className="text-sm font-medium">
                Eingehende Mails per IMAP abrufen (alle 60s)
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Backend pollt alle 60 Sekunden die INBOX über IMAP, holt
                ungelesene Mails und beantwortet sie analog zum
                WhatsApp-Webhook. Voraussetzung: Gmail-Adresse und App-Passwort
                oben gesetzt, IMAP in Gmail-Einstellungen aktiviert.
              </p>
            </div>
          </div>
          <div className="md:col-span-2 p-4 rounded-xl border border-border-soft bg-secondary/40">
            <div className="overline mb-2">Inbound Email Webhook (optional)</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs font-mono break-all bg-white border border-border-soft rounded-lg px-3 py-2">
                POST {webhookBase}/email
              </code>
              <button
                className="btn-ghost"
                onClick={() => copy(`${webhookBase}/email`)}
                data-testid="copy-email-webhook"
              >
                <Copy size={14} weight="bold" /> Copy
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              Post JSON <code>{"{ from_email, from_name, subject, body, message_id }"}</code>{" "}
              to this endpoint from Gmail Apps Script, SendGrid Inbound Parse,
              or Mailgun. The bot will auto-reply from your Gmail address above.
            </p>
          </div>
          <div className="md:col-span-2 text-xs text-muted-foreground">
            Create an App Password in your Google Account → Security →
            2-Step Verification → App Passwords. Requires 2FA enabled.
          </div>
        </div>
      </section>

      {/* Notifications */}
      <section className="card-soft mb-6 overflow-hidden" data-testid="settings-notifications">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border-soft bg-secondary/30">
          <Bell size={18} weight="bold" />
          <div className="overline">Escalation Notifications</div>
        </div>
        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="overline block mb-2">
              Your email (for escalation alerts)
            </label>
            <input
              data-testid="owner-email"
              type="email"
              value={data.owner_notification_email || ""}
              onChange={(e) =>
                update("owner_notification_email", e.target.value)
              }
              placeholder="owner@yourbusiness.com"
              className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                data-testid="enable-notifications"
                type="checkbox"
                checked={data.escalation_notifications_enabled ?? true}
                onChange={(e) =>
                  update("escalation_notifications_enabled", e.target.checked)
                }
                className="w-4 h-4"
              />
              <span className="text-sm">
                Email me whenever the bot escalates a message
              </span>
            </label>
          </div>
        </div>
      </section>

      {/* DSGVO Auskunft + Löschung */}
      <section className="card-soft mb-6 overflow-hidden" data-testid="settings-dsgvo">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border-soft bg-secondary/30">
          <Scales size={18} weight="bold" />
          <div className="overline">DSGVO Auskunft &amp; Löschung</div>
        </div>
        <div className="p-4 sm:p-6 space-y-5">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Erfüllt Art. 15 (Auskunft) und Art. 17 (Löschung) DSGVO. Wenn ein
            Kunde anfragt, was über ihn gespeichert ist oder seine Daten gelöscht
            haben will, hier die Telefonnummer (mit <code>+</code>) oder Email
            eingeben. Auto-Delete nach 90 Tagen läuft zusätzlich automatisch
            via DB-Cron.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="overline block mb-2">
                Telefonnummer oder Email der betroffenen Person
              </label>
              <input
                data-testid="dsgvo-sender-id"
                value={dsgvo.sender_id}
                onChange={(e) =>
                  setDsgvo((s) => ({ ...s, sender_id: e.target.value, lastResult: null }))
                }
                placeholder="+49170... oder kunde@example.de"
                className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 font-mono transition-all"
              />
            </div>
            <div>
              <label className="overline block mb-2">Channel</label>
              <select
                data-testid="dsgvo-channel"
                value={dsgvo.channel}
                onChange={(e) => setDsgvo((s) => ({ ...s, channel: e.target.value }))}
                className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white"
              >
                <option value="all">Alle</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
                <option value="simulator">Simulator</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="dsgvo-export-btn"
              className="btn-ghost"
              disabled={dsgvo.busy || !dsgvo.sender_id.trim()}
              onClick={handleDsgvoExport}
            >
              <DownloadSimple size={16} weight="bold" /> Auskunft exportieren (JSON)
            </button>
            <button
              data-testid="dsgvo-delete-btn"
              className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 inline-flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={dsgvo.busy || !dsgvo.sender_id.trim()}
              onClick={() => setDsgvo((s) => ({ ...s, confirmDelete: true }))}
            >
              <Trash size={16} weight="bold" /> Daten löschen
            </button>
          </div>

          {dsgvo.confirmDelete && (
            <div
              className="p-4 rounded-xl border border-red-300 bg-red-50/70"
              data-testid="dsgvo-confirm"
            >
              <div className="flex items-start gap-3">
                <Warning size={20} weight="bold" className="text-red-600 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-red-900 mb-1">
                    Wirklich löschen?
                  </div>
                  <p className="text-xs text-red-800 leading-relaxed mb-3">
                    Alle Konversationen und Nachrichten von{" "}
                    <code className="font-mono">{dsgvo.sender_id}</code>
                    {dsgvo.channel !== "all" && (
                      <>
                        {" "}auf Channel <code>{dsgvo.channel}</code>
                      </>
                    )}{" "}
                    werden unwiderruflich gelöscht. Diese Aktion kann nicht
                    rückgängig gemacht werden.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="dsgvo-confirm-yes"
                      className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors"
                      onClick={handleDsgvoForget}
                      disabled={dsgvo.busy}
                    >
                      Ja, endgültig löschen
                    </button>
                    <button
                      className="btn-ghost text-xs"
                      onClick={() => setDsgvo((s) => ({ ...s, confirmDelete: false }))}
                      disabled={dsgvo.busy}
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {dsgvo.lastResult?.type === "forget" && (
            <div className="text-xs text-muted-foreground" data-testid="dsgvo-result-forget">
              Letzte Löschung: {dsgvo.lastResult.conversations} Konversation(en),{" "}
              {dsgvo.lastResult.messages} Nachricht(en).
            </div>
          )}
          {dsgvo.lastResult?.type === "export" && (
            <div className="text-xs text-muted-foreground" data-testid="dsgvo-result-export">
              Letzter Export: {dsgvo.lastResult.conversations} Konversation(en),{" "}
              {dsgvo.lastResult.messages} Nachricht(en) — JSON heruntergeladen.
            </div>
          )}
        </div>
      </section>

      <div className="flex items-center justify-end gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck size={14} weight="bold" /> Secrets stored server-side
        </div>
        <button className="btn-invert" onClick={save} data-testid="save-settings-btn">
          <FloppyDisk size={16} weight="bold" /> Einstellungen speichern
        </button>
      </div>
    </div>
  );
}
